/**
 * @module services/patient-padron
 * @description UC-REG-317 (Campana, Diego 2026-11-04) — Alta de paciente por
 *              el staff con autocompletado desde el padrón del Municipio:
 *              el médico/admisionista escribe el DNI, `POST /patients/lookup`
 *              devuelve la(s) persona(s) del padrón y el form se completa
 *              (nombre, apellido, sexo, fecha de nacimiento, CUIL, domicilio)
 *              sin tipear. Disponible solo cuando la API expone
 *              `capabilities.padronStaffLookup` (perfil del tenant + credenciales
 *              cargadas en la organización); el Dashboard solo muestra u oculta.
 */
import api from "@/services/api";
import { hasCapabilityIn } from "@/services/capabilities";

/** @type {string} Clave de la capacidad del tenant. */
export const CAPABILITY = "padronStaffLookup";

/** @type {Object<string,string>} Mensajes por código de error de la API. */
export const LOOKUP_MESSAGES = {
  InvalidDni: "Ingresá un DNI válido (7 u 8 dígitos).",
  PersonNotFound: "El DNI no figura en el padrón del Municipio.",
  LookupNotAvailable: "La búsqueda en el padrón no está disponible.",
  PadronUnavailable:
    "El padrón del Municipio no responde. Completá los datos a mano o probá más tarde.",
  TooManyLookups: "Demasiadas búsquedas seguidas. Esperá unos minutos.",
};

/**
 * @description ¿El tenant tiene la búsqueda en el padrón para el staff?
 * @param {Object|null} organizationContext - `store.state.organizationContext`
 * @returns {boolean}
 */
export function hasPadronStaffLookupCapability(organizationContext) {
  return hasCapabilityIn(organizationContext, CAPABILITY);
}

/**
 * @description Normaliza y valida un DNI (7 u 8 dígitos).
 * @param {string|number} value
 * @returns {string} dígitos, o '' si no es válido
 */
export function normalizeDni(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return /^\d{7,8}$/.test(digits) ? digits : "";
}

/**
 * @description Mensaje para mostrar ante un error del lookup.
 * @param {Object} err - error de axios
 * @returns {string}
 */
export function lookupErrorMessage(err) {
  const code =
    err && err.response && err.response.data && err.response.data.error;
  return LOOKUP_MESSAGES[code] || "No pudimos consultar el padrón.";
}

/**
 * @description Busca el DNI en el padrón.
 * @param {string} dni
 * @returns {Promise<{persons: Object[], warnings: string[]}>}
 */
export function lookupByDni(dni) {
  return api
    .post("/patients/lookup", { dni }, { getErrorMessage: () => null })
    .then((r) => r.data);
}

/**
 * @description Texto para elegir entre varias personas del padrón (varias CUIL).
 * @param {Object} person - persona de la API
 * @returns {string}
 */
export function personLabel(person) {
  const name = [person.lastName, person.firstName].filter(Boolean).join(", ");
  const extra = [
    person.cuil ? `CUIL ${person.cuil}` : "",
    person.bornAt ? `nac. ${person.bornAt.split("-").reverse().join("/")}` : "",
    person.deceased ? "fallecido/a" : "",
  ]
    .filter(Boolean)
    .join(" · ");
  return extra ? `${name} — ${extra}` : name;
}

/**
 * @description Campos del alta completa (`PatientDetail.vue`) a partir de la
 *              persona del padrón. `bornAt` como Date (el date-picker del
 *              form no tiene value-format); domicilio al shape del form.
 * @param {Object} person - persona de la API
 * @returns {Object} parcial para mergear sobre `patient`
 */
export function personToDetailForm(person) {
  const out = {
    firstName: person.firstName || "",
    lastName: person.lastName || "",
  };
  if (person.gender) out.gender = person.gender;
  if (person.bornAt) out.bornAt = new Date(`${person.bornAt}T12:00:00`);
  if (person.cuil) out.taxId = { type: "cuil", number: person.cuil };
  if (person.address) {
    out.address = {
      street: person.address.street || "",
      buildingNumber: person.address.buildingNumber || "",
      apartmentFloor: person.address.apartmentFloor || "",
      apartmentNumber: person.address.apartmentNumber || "",
      city: person.address.city || "",
      postalCode: person.address.postalCode || "",
      district: person.address.district || "",
    };
  }
  return out;
}

/**
 * @description Campos del alta rápida (`PatientQuickForm.vue`) a partir de la
 *              persona del padrón. `bornAt` como 'yyyy-MM-dd' (value-format).
 * @param {Object} person - persona de la API
 * @returns {Object} parcial para mergear sobre `patient`
 */
export function personToQuickForm(person) {
  const out = {
    firstName: person.firstName || "",
    lastName: person.lastName || "",
  };
  if (person.gender) out.gender = person.gender;
  if (person.bornAt) out.bornAt = person.bornAt;
  return out;
}

export default {
  CAPABILITY,
  LOOKUP_MESSAGES,
  hasPadronStaffLookupCapability,
  normalizeDni,
  lookupErrorMessage,
  lookupByDni,
  personLabel,
  personToDetailForm,
  personToQuickForm,
};
